import { z } from 'zod'

import DataSource from 'constants/sources/DataSource'

// TODO: this enum might change when airflow work is completed
export enum AirflowJobStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Completed = 'Completed',
  Errored = 'Errored',
}

export enum TransactionType {
  In = 'In',
  Out = 'Out',
  Internal = 'Internal',
  External = 'External',
  Swap = 'Swap',
  Ignore = 'Ignore',
}

export const transactionExportSchema = z.object({
  url: z.string(),
})

export const transactionCountSchema = z.object({
  result: z.string(),
})

export const bulkEditStatusSchema = z.object({
  result: z.array(
    z.object({
      job_id: z.string(),
      edit_status: z.nativeEnum(AirflowJobStatus),
    })
  ),
})

export const remoteDataSchema = z.object({
  data: z
    .object({
      SourceID: z.string(),
    })
    .optional(),
})

export const journalEntrySchema = z.object({
  Adjustment: z.boolean().optional(),
  CurrencyRef: z.record(z.string()).optional(),
  DocNumber: z.string().optional(),
  ExchangeRate: z.number().optional(),
  Id: z.string(),
  RemoteId: z.string().optional(),
  MetaData: z.record(z.string()),
  SyncToken: z.string(),
  TotalAmt: z.number().optional(),
  TxnDate: z.string(),
  domain: z.string().optional(),
  sparse: z.boolean().optional(),
  RemoteData: z.array(remoteDataSchema).optional(),
})

export const dataSourceSchema = z.nativeEnum(DataSource)

export const accountingExportStatusSchema = z.object({
  application: z.string(),
  accountEntry: journalEntrySchema,
  lastSyncedDate: z.string().optional(),
  message: z.string().optional(),
  status: z.string().optional(),
  journalId: z.string().optional(),
  isBulk: z.boolean(),
  syncEligible: z.boolean(),
  date: z.string().optional(),
  createdAt: z.string().optional(),
})

const transactionAnnotationSchema = z.object({
  category: z.string(),
  memo: z.string(),
  transactionFunction: z.string(),
})

const transactionDataSourceSchema = z.object({
  source: dataSourceSchema,
  label: z.string(),
})

const assetQuantitySchema = z.object({
  asset: z.string(),
  amount: z.number(),
  contractAddress: z.string().nullable().optional(),
})

const transactionValueSchema = z.object({
  value: assetQuantitySchema,
  transactionCost: assetQuantitySchema.nullable(),
  convertedValues: z.array(assetQuantitySchema),
  convertedCosts: z.array(assetQuantitySchema),
})

const gainLossSchema = z.object({
  isFee: z.boolean(),
  costBasis: z.number(),
  displayGainLoss: z.number(),
  calculatedGainLoss: z.number(),
})

const addressSchema = z.object({
  addr: z.string(),
  label: z.string(),
})

const groupedTransactionSchema = z.object({
  id: z.string(),
  groupId: z.string(),
  parentId: z.string(),
  versionId: z.string(),
  customFields: z.array(z.object({ field_id: z.string(), value: z.string() })),
  dataSource: transactionDataSourceSchema,
  timestamp: z.string(),
  fromAddress: addressSchema,
  toAddress: addressSchema,
  value: transactionValueSchema,
  transactionType: z.string(),
  groupTransactionType: z.string(),
  isMain: z.boolean().optional(),
  annotation: transactionAnnotationSchema,
  mainGainLoss: gainLossSchema.nullable().optional(),
  feeGainLoss: gainLossSchema.nullable().optional(),
  accountingStatus: z.array(accountingExportStatusSchema),
  isError: z.boolean(),
  status: z.string(),
  transferType: z.string(),
  tokenId: z.string().nullable().optional(),
  usdValue: z.number().optional(),
  usdFee: z.number().optional(),
})

export const transactionSchema = z.object({
  id: z.string(),
  groupId: z.string(),
  parentId: z.string(),
  versionId: z.string(),
  dataSource: transactionDataSourceSchema,
  customFields: z.array(z.object({ field_id: z.string(), value: z.string() })),
  timestamp: z.string(),
  fromAddress: addressSchema,
  toAddress: addressSchema,
  value: transactionValueSchema,
  transactionType: z.string(),
  groupTransactionType: z.string(),
  group: z.array(groupedTransactionSchema).optional(),
  isMain: z.boolean().optional(),
  annotation: transactionAnnotationSchema,
  mainGainLoss: gainLossSchema.nullable().optional(),
  feeGainLoss: gainLossSchema.nullable().optional(),
  accountingStatus: z.array(accountingExportStatusSchema),
  isError: z.boolean(),
  status: z.string(),
  transferType: z.string().nullable(),
  tokenId: z.string().nullable().optional(),
  usdValue: z.number().optional(),
  usdFee: z.number().optional(),
})

export type Transaction = z.infer<typeof transactionSchema>

export const transactionTypeEnumSchema = z.nativeEnum(TransactionType)

export const transactionTypeSchema = z.object({
  types: z.array(transactionTypeEnumSchema),
})

export const categoryMetadataSchema = z.object({
  categoriesGrouped: z.record(z.nativeEnum(TransactionType), z.array(z.string())),
  categoryDescriptions: z.record(z.string(), z.string()),
  categoryPriorities: z.record(z.string(), z.boolean()),
})

export const groupTransactionTypeSchema = z.array(z.string())
